import type { AwardCheck } from '@myrewards/contracts';
import type { HeroSteps } from '../../shell';
import { formatDate, formatMoney, formatRecentTime, plural, type StatusTone } from '../../ui';
import { amountWords, costTypeWord } from '../labels';

type CheckLine = AwardCheck['lines'][number];
type CheckItem = AwardCheck['checklist'][number];

/**
 * Where the signed-in Stock Admin stands with this check:
 * - `yours`: they hold the claim and the check is open (they can change it);
 * - `yours_done`: they hold it, but the check is complete (the award is at letters);
 * - `held`: a teammate holds it;
 * - `open`: nobody holds it. With the Stock Admin team, anyone on the team can claim it there,
 *   whether it's waiting in the queue or was handed back mid-check (APP-039).
 */
export type CheckMode = 'yours' | 'yours_done' | 'held' | 'open';

export function checkMode(check: AwardCheck, meId: string): CheckMode {
  if (check.yours) return 'yours';
  if (check.claimedBy === null) return 'open';
  return check.claimedBy.id === meId ? 'yours_done' : 'held';
}

/**
 * Nobody has claimed the award and its check isn't done (QA-015): the checklist shows what the
 * check covers, numbered and unticked, so nothing reads as already checked.
 */
export function notYetClaimed(check: Pick<AwardCheck, 'completed'>, mode: CheckMode): boolean {
  return mode === 'open' && !check.completed;
}

/** "Naomi Castellanos · Oct 12, 2026", or why there's no approval to show. */
export function approvalWords(check: Pick<AwardCheck, 'approved'>): string {
  if (check.approved === null) return 'Didn’t need one';
  const on = formatDate(check.approved.at);
  return check.approved.by === null ? on : `${check.approved.by.name} · ${on}`;
}

/**
 * Who the letters go to, as chosen when the award was entered (APP-057): one holder by name, each
 * person their own, or several counted. An award entered before the choice existed has none yet;
 * the award type's default applies when the letters are made.
 */
export function letterWords(check: Pick<AwardCheck, 'lines'>): string {
  const held = check.lines.flatMap((l) =>
    l.letterRecipient === null ? [] : [{ person: l.person.id, holder: l.letterRecipient }],
  );
  if (held.length === 0) return 'The award type’s default, when the letters are made';
  const missing = held.length < check.lines.length;
  const own = held.every((h) => h.holder.id === h.person);
  if (own && !missing)
    return check.lines.length === 1
      ? `${held[0]?.holder.name ?? ''} · the employee`
      : 'Each person gets their own';
  const names = [...new Set(held.map((h) => h.holder.name))];
  const [only] = names;
  if (names.length === 1 && only !== undefined && !missing) return only;
  return missing
    ? `${plural(names.length, 'holder', 'holders')} so far · the rest when the letters are made`
    : plural(names.length, 'letter holder', 'letter holders');
}

/** "The check · Jordan Ellis" for one person, else the award's title, program or number. */
export function checkSubject(check: AwardCheck): string {
  const [only] = check.lines;
  if (check.lines.length === 1 && only) return only.person.name;
  return check.award.title ?? check.award.program?.name ?? check.award.displayId ?? 'This award';
}

const ITEM_LABEL: Record<CheckItem['id'], string> = {
  lines: 'Lines',
  fmv: 'FMV',
  coding: 'Coding',
  proof: 'Proof',
  tax: 'Tax',
};

export const ITEM_STATUS: Record<CheckItem['status'], { label: string; tone: StatusTone }> = {
  done: { label: 'Done', tone: 'done' },
  open: { label: 'To do', tone: 'waiting' },
  not_needed: { label: 'Not needed', tone: 'neutral' },
};

/** "0535-006 · org 1.1.30.4 · no project" (project numbers are text: 15125.110 keeps its zero). */
export function codingWords(line: CheckLine): string {
  if (line.coding === null) return 'No charge code yet';
  return [
    `${costTypeWord(line.costType)} · ${line.coding.account}`,
    `org ${line.coding.org}`,
    line.coding.project === null ? 'no project' : `project ${line.coding.project}`,
  ].join(' · ');
}

function totals(lines: readonly CheckLine[]): { cash: number; options: number; value: number } {
  return lines.reduce(
    (sum, l) => ({
      cash: sum.cash + l.cashCents,
      options: sum.options + l.options,
      value: sum.value + (l.totalValueCents ?? 0),
    }),
    { cash: 0, options: 0, value: 0 },
  );
}

/** One line of the checklist: its label, status words and the detail under it. */
export function checklistRow(
  item: CheckItem,
  check: AwardCheck,
): { label: string; status: (typeof ITEM_STATUS)[CheckItem['status']]; detail: string } {
  const status = ITEM_STATUS[item.status];
  // APP-151: a PAA's options are counted in the value of one option, checked with the FMV.
  const label =
    item.id === 'fmv' && check.optionValue !== null ? 'FMV and option value' : ITEM_LABEL[item.id];
  const t = totals(check.lines);
  const people = plural(check.lines.length, 'person', 'people');
  switch (item.id) {
    case 'lines': {
      const value = t.value > 0 ? ` · ${formatMoney(t.value)} value` : '';
      return { label, status, detail: `${people}${value} · ${amountWords(t.cash, t.options)}` };
    }
    case 'fmv': {
      if (item.status === 'not_needed')
        return { label, status, detail: 'No options on this award' };
      const used = `${formatMoney(check.fmv.used.cents)} per share`;
      const option = check.optionValue;
      if (option !== null) {
        const perOption = `${formatMoney(option.used.cents)} an option`;
        if (check.fmv.matches && option.matches)
          return { label, status, detail: `${perOption} and ${used}, the values in effect` };
        const changed = [
          ...(option.matches
            ? []
            : [
                option.current
                  ? `Sent with ${perOption}; it’s ${formatMoney(option.current.cents)} now`
                  : `Sent with ${perOption}`,
              ]),
          ...(check.fmv.matches
            ? []
            : [
                check.fmv.current
                  ? `Sent with ${used}; it’s ${formatMoney(check.fmv.current.cents)} now`
                  : `Sent with ${used}`,
              ]),
        ];
        return { label, status, detail: changed.join(' · ') };
      }
      if (check.fmv.matches) return { label, status, detail: `${used}, the FMV in effect` };
      return {
        label,
        status,
        detail: check.fmv.current
          ? `Sent with ${used}; it’s ${formatMoney(check.fmv.current.cents)} now`
          : `Sent with ${used}`,
      };
    }
    case 'coding': {
      if (item.status !== 'done') return { label, status, detail: 'A line has no charge code yet' };
      const [only] = check.lines;
      const switched = check.lines.filter((l) => l.costTypeSwitched).length;
      if (check.lines.length === 1 && only) return { label, status, detail: codingWords(only) };
      return {
        label,
        status,
        detail: `${people} coded${switched > 0 ? ` · ${switched} switched at the check` : ''}`,
      };
    }
    case 'proof': {
      if (check.proof.needed === null)
        return { label, status, detail: 'Not needed for this award type' };
      if (check.proof.needed === 'write_up')
        return {
          label: 'Write-up',
          status,
          detail: check.proof.present
            ? 'Write-up on the award'
            : 'Add the write-up: typed in or attached',
        };
      return {
        label,
        status,
        detail: check.proof.present
          ? 'Approval proof attached and checked'
          : 'Attach the approval proof',
      };
    }
    case 'tax': {
      if (item.status === 'not_needed') return { label, status, detail: 'No cash election here' };
      // APP-101: entered under Tax to enter, by anyone on the team; it holds the letters.
      const taxed = check.lines.filter((l) => l.cashCents > 0);
      const entered = taxed.filter((l) => l.taxEstimates !== null).length;
      return {
        label,
        status,
        detail:
          item.status === 'done'
            ? 'Everyone’s tax entered · Estimate'
            : `${String(entered)} of ${String(taxed.length)} entered under Tax to enter · the letters wait on it`,
      };
    }
  }
}

/**
 * The hero's checklist count (QA-030): the server's items, done or not needed, out of all of them.
 * There are no tick boxes to count (APP-105): **Mark checked** is the confirmation, so once
 * nothing is left the award is ready to mark checked, by its holder or, from the queue, by anyone
 * on the team when nobody holds it.
 */
export function checkProgress(check: AwardCheck, mode: CheckMode): { value: string; meta: string } {
  // APP-101: the tax holds the letters, not the check, so it isn't counted here.
  const checklist = check.checklist.filter((c) => c.id !== 'tax');
  const items = checklist.length;
  const done = checklist.filter((c) => c.status !== 'open').length;
  const left = items - done;
  const value = `${String(done)} of ${String(items)}`;
  if (left > 0) return { value, meta: `${plural(left, 'item', 'items')} still to do` };
  if (mode === 'yours_done') return { value, meta: 'Nothing left to check' };
  return { value, meta: 'Ready to mark checked' };
}

/**
 * Where the award stands in the Stock Admin team's steps (the SA2/SA3 boards' tracker): Check,
 * then Tax when it has an election to estimate, then Letters. The tax can be entered at any time
 * (APP-101), so Tax is done as soon as everyone has a figure; once the check is complete, the
 * first step not done is current.
 */
export function workSteps(
  check: Pick<AwardCheck, 'checklist' | 'completed'>,
  at: 'check' | 'letters',
  released = false,
): HeroSteps['steps'] {
  const tax = check.checklist.find((c) => c.id === 'tax');
  const taxNeeded = tax !== undefined && tax.status !== 'not_needed';
  const taxDone = !taxNeeded || tax.status === 'done' || released;
  const checked = check.completed || at === 'letters';
  const steps: HeroSteps['steps'][number][] = [
    { label: 'Check', state: checked ? 'done' : 'current' },
  ];
  if (taxNeeded)
    steps.push({ label: 'Tax', state: taxDone ? 'done' : checked ? 'current' : 'next' });
  steps.push({
    label: 'Letters',
    state: released ? 'done' : checked && taxDone ? 'current' : 'next',
  });
  return steps;
}

/**
 * The check's hero (SA2): who holds it and since when, its steps, and what's left, in words.
 * "Claimed by you · Today, 9:41 AM PDT", then "You can change this check · 2 confirmations left".
 */
export function checkHeroSteps(
  check: AwardCheck,
  mode: CheckMode,
  progress: { meta: string },
  now: Date = new Date(),
): HeroSteps {
  const since = check.claimedAt === null ? '' : ` · ${formatRecentTime(check.claimedAt, now)}`;
  const label =
    mode === 'yours' || mode === 'yours_done'
      ? `Claimed by you${since}`
      : mode === 'held' && check.claimedBy !== null
        ? `Claimed by ${check.claimedBy.name}${since}`
        : 'Not claimed yet';
  const meta =
    mode === 'yours'
      ? `You can change this check · ${progress.meta}`
      : mode === 'yours_done'
        ? 'Marked checked · at letters'
        : mode === 'held'
          ? `Read-only for you · ${progress.meta}`
          : notYetClaimed(check, mode)
            ? `Claim it to start the check · ${plural(check.checklist.length, 'item', 'items')} to check`
            : 'Claim it to take it over';
  return { label, steps: workSteps(check, 'check'), meta };
}

/**
 * Each pool and what it pays: the "Paid from" list (never the cash/options fraction), from the
 * check's lines or a queue row's people (APP-109).
 */
export function paidFrom(
  lines: readonly Pick<CheckLine, 'pool' | 'cashCents' | 'options'>[],
): { pool: { id: string; name: string }; people: number; cashCents: number; options: number }[] {
  const pools = new Map<
    string,
    { pool: { id: string; name: string }; people: number; cashCents: number; options: number }
  >();
  for (const line of lines) {
    const row = pools.get(line.pool.id) ?? { pool: line.pool, people: 0, cashCents: 0, options: 0 };
    row.people += 1;
    row.cashCents += line.cashCents;
    row.options += line.options;
    pools.set(line.pool.id, row);
  }
  return [...pools.values()];
}
